import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, ChevronRightIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {
    opacity: 0
  },
  whileInView: {
    opacity: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    staggerChildren: 0.2
  }
};
const articles = [{
  title: 'Understanding Property Transfers in Botswana',
  excerpt: 'A comprehensive guide to the conveyancing process, from signing the deed of sale to final registration at the Deeds Office.',
  date: 'October 15, 2023',
  author: 'Sarah Tlale',
  category: 'Conveyancing',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeefa?auto=format&fit=crop&q=80&w=800'
}, {
  title: 'Preparing for Divorce Proceedings',
  excerpt: 'Essential steps to take and documents to gather when considering or facing divorce proceedings in Botswana.',
  date: 'September 28, 2023',
  author: 'David Kgosi',
  category: 'Family Law',
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800'
}, {
  title: 'Understanding Child Maintenance',
  excerpt: 'Legal obligations, calculation methods, and enforcement of child maintenance orders under Botswana law.',
  date: 'September 10, 2023',
  author: 'David Kgosi',
  category: 'Family Law',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800'
}, {
  title: 'Debt Recovery Explained',
  excerpt: 'The legal processes available to businesses and individuals for recovering outstanding debts efficiently.',
  date: 'August 22, 2023',
  author: 'Charles McErick',
  category: 'Commercial Law',
  image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800'
}, {
  title: 'Buying Property Safely',
  excerpt: 'Red flags to watch out for and due diligence required before signing a property purchase agreement.',
  date: 'August 05, 2023',
  author: 'Sarah Tlale',
  category: 'Property Law',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=800'
}, {
  title: 'Disciplinary Hearings in the Workplace',
  excerpt: 'A guide for employers on conducting fair and legally compliant disciplinary hearings.',
  date: 'July 18, 2023',
  author: 'Lerato Modise',
  category: 'Labour Law',
  image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=800'
}];
export function Insights() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-350">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-351">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-352">
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2000" alt="Legal professionals at work" className="w-full h-full object-cover" data-id="element-353" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-354">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-6" data-id="element-355">
            Legal Insights
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-356">
            Expert commentary, legal updates, and practical guidance on matters
            affecting you and your business.
          </motion.p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-24" data-id="element-357">
        <div className="max-w-container mx-auto px-6" data-id="element-358">
          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-359">
            {articles.map((article, idx) => <motion.article key={idx} variants={fadeInUp} className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-card transition-shadow duration-300 flex flex-col h-full" data-id="element-360">
                <div className="relative h-48 overflow-hidden" data-id="element-361">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-362" />
                  <div className="absolute top-4 left-4 bg-brand-gold text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-363">
                    {article.category}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow" data-id="element-364">
                  <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-3 hover:text-brand-green transition-colors" data-id="element-365">
                    <Link to="#" data-id="element-366">{article.title}</Link>
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 flex-grow leading-relaxed" data-id="element-367">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100 mt-auto" data-id="element-368">
                    <div className="flex items-center" data-id="element-369">
                      <CalendarIcon className="w-4 h-4 mr-1 text-brand-gold" data-id="element-370" />
                      {article.date}
                    </div>
                    <div className="flex items-center" data-id="element-371">
                      <UserIcon className="w-4 h-4 mr-1 text-brand-gold" data-id="element-372" />
                      {article.author}
                    </div>
                  </div>
                </div>
              </motion.article>)}
          </motion.div>
        </div>
      </section>
    </div>;
}